// API endpoints
export const API_ENDPOINTS = {
  AUTH: {
    LOGIN: '/auth/login',
    REGISTER: '/auth/register',
    LOGOUT: '/auth/logout',
    CURRENT_USER: '/auth/me',
    FORGOT_PASSWORD: '/auth/forgot-password',
    RESET_PASSWORD: '/auth/reset-password',
    VERIFY_EMAIL: '/auth/verify-email',
  },
  ASSETS: {
    BASE: '/assets',
    BY_ID: (id: string) => `/assets/${id}`,
    BY_CATEGORY: (category: string) => `/assets/category/${category}`,
    BY_LOCATION: (location: string) => `/assets/location/${location}`,
    BY_STATUS: (status: string) => `/assets/status/${status}`,
    ASSIGN: (id: string) => `/assets/${id}/assign`,
    UNASSIGN: (id: string) => `/assets/${id}/unassign`,
  },
  MAINTENANCE: {
    BASE: '/maintenance',
    BY_ASSET: (assetId: string) => `/maintenance/asset/${assetId}`,
  },
  USERS: {
    BASE: '/users',
    BY_ID: (id: string) => `/users/${id}`,
  },
  REPORTS: {
    BASE: '/reports',
    ASSET_SUMMARY: '/reports/asset-summary',
    MAINTENANCE_SUMMARY: '/reports/maintenance-summary',
  },
};

// Asset categories
export const ASSET_CATEGORIES = [
  'Laptop',
  'Desktop',
  'Mobile Phone',
  'Tablet',
  'Printer',
  'Scanner',
  'Display',
  'Networking',
  'Server',
  'Storage',
  'Furniture',
  'Vehicle',
  'Other',
];

// Asset statuses
export const ASSET_STATUSES = [
  { value: 'active', label: 'Active', color: 'success' },
  { value: 'inactive', label: 'Inactive', color: 'error' },
  { value: 'maintenance', label: 'Maintenance', color: 'warning' },
];

// Maintenance types
export const MAINTENANCE_TYPES = [
  { value: 'Preventive', label: 'Preventive' },
  { value: 'Repair', label: 'Repair' },
  { value: 'Inspection', label: 'Inspection' },
];

// Pagination defaults
export const PAGINATION = {
  DEFAULT_PAGE: 0,
  DEFAULT_ROWS_PER_PAGE: 10,
  ROWS_PER_PAGE_OPTIONS: [5, 10, 25, 50],
};

// Local storage keys
export const STORAGE_KEYS = {
  TOKEN: 'token',
  USER: 'user',
  THEME: 'theme',
  LANGUAGE: 'language',
};

// Theme options
export const THEME_OPTIONS = {
  LIGHT: 'light',
  DARK: 'dark',
  SYSTEM: 'system',
};

// Date formats
export const DATE_FORMATS = {
  DISPLAY: 'MMM dd, yyyy',
  INPUT: 'yyyy-MM-dd',
  API: 'yyyy-MM-dd',
  DISPLAY_WITH_TIME: 'MMM dd, yyyy HH:mm',
  API_WITH_TIME: "yyyy-MM-dd'T'HH:mm:ss.SSS'Z'",
};

// Error messages
export const ERROR_MESSAGES = {
  GENERIC: 'An error occurred. Please try again.',
  NETWORK: 'Network error. Please check your connection.',
  UNAUTHORIZED: 'You are not authorized to perform this action.',
  NOT_FOUND: 'The requested resource was not found.',
  VALIDATION: 'Please check your input and try again.',
  SERVER: 'Server error. Please try again later.',
};

// Success messages
export const SUCCESS_MESSAGES = {
  ASSET_CREATED: 'Asset created successfully.',
  ASSET_UPDATED: 'Asset updated successfully.',
  ASSET_DELETED: 'Asset deleted successfully.',
  MAINTENANCE_CREATED: 'Maintenance record created successfully.',
  MAINTENANCE_UPDATED: 'Maintenance record updated successfully.',
  MAINTENANCE_DELETED: 'Maintenance record deleted successfully.',
  PASSWORD_RESET: 'Password reset successfully.',
  PROFILE_UPDATED: 'Profile updated successfully.',
};

// Routes
export const ROUTES = {
  HOME: '/',
  DASHBOARD: '/dashboard',
  LOGIN: '/auth/login',
  REGISTER: '/auth/register',
  FORGOT_PASSWORD: '/auth/forgot-password',
  RESET_PASSWORD: '/auth/reset-password',
  VERIFY_EMAIL: '/auth/verify-email',
  ASSETS: {
    LIST: '/assets',
    CREATE: '/assets/create',
    DETAILS: (id: string) => `/assets/${id}`,
    EDIT: (id: string) => `/assets/${id}/edit`,
  },
  MAINTENANCE: {
    LIST: '/maintenance',
    CREATE: '/maintenance/create',
    DETAILS: (id: string) => `/maintenance/${id}`,
    EDIT: (id: string) => `/maintenance/${id}/edit`,
  },
  REPORTS: '/reports',
  SETTINGS: '/settings',
  PROFILE: '/profile',
  NOT_FOUND: '/404',
};

export const allChartTypes = [
  //Assets
  'asset_status_breakdown',
  'asset_condition',
  'asset_location',
  'asset_category',
  'department',

  //Transfers
  'source_location',
  'source_category',
  'source_department',
  'destination_location',
  'destination_department',
  'at_asset_status',
  'at_status',
  'transfer_cost_source_category',
  'transfer_cost_source_department',

  //workflow
  'user_role',
  'request_type',
  'pending_and_approved',

  //Disposals
  'disposal_location',
  'disposal_category',
  'disposal_department',
  'disposal_asset_status',
  'consolidated_category',
  'consolidated_location',
  'consolidated_department',

  //Audits
  'breakdown_audit_status',
  'breakdown_audit_request',

  //Schedule
  'status_activity_type',
  'status_user_group',
  'sla',

  //Cost
  'activity_type',
  'user_group',
];

export const assetReportCharts = [
  'asset_status_breakdown',
  'asset_condition',
  'asset_location',
  'asset_category',
  'department',
];
export const assetDisposalCharts = [
  'disposal_location',
  'disposal_category',
  'disposal_department',
  'disposal_asset_status',
  'consolidated_category',
  'consolidated_location',
  'consolidated_department',
];
export const assetTransferCharts = [
  'source_location',
  'source_category',
  'source_department',
  'destination_location',
  'destination_department',
  'at_asset_status',
  'at_status',
  'transfer_cost_source_category',
  'transfer_cost_source_department',
];
export const auditBreakdownsCharts = [
  'breakdown_audit_status',
  'breakdown_audit_request',
];
export const activityStatusCharts = [
  'status_activity_type',
  'status_user_group',
  'sla',
];
export const totalCostCharts = ['activity_type', 'user_group'];
export const workflowCharts = [
  'user_role',
  'request_type',
  'pending_and_approved',
];

export const USER_GUIDE_ROUTES: Array<{
  match: string | string[];
  path: string;
}> = [
  {
    match: [
      '/asset-statuses',
      '/asset-conditions',
      '/asset-categories',
      '/departments',
      '/manufacturers',
      '/models',
      '/suppliers',
      '/sites',
      '/locations',
    ],
    path: '/02-master-data-configuration/',
  },
  { match: '/assets', path: '/03-asset-management/' },
  { match: '/asset-transfer', path: '/04-asset-transfer/' },
  { match: '/asset-disposal', path: '/05-asset-disposal/' },
  { match: '/audit', path: '/06-audit-management/' },
  {
    match: [
      '/schedule-management',
      '/user-groups',
      '/activity-types',
      '/checklist-templates',
      '/cost-templates',
    ],
    path: '/07-schedule-activity-management/',
  },
  { match: '/depreciation', path: '/08-depreciation-management/' },
  { match: '/rfid', path: '/09-rfid-management/' },
  {
    match: ['/floor-plans', '/interactive-editor'],
    path: '/10-floor-plan-management/',
  },
  {
    match: ['/workflow-templates', '/approval-tasks'],
    path: '/11-workflow-management/',
  },
  { match: '/reports', path: '/12-reports/' },
  { match: '/bulk-upload', path: '/13-bulk-upload/' },
  { match: ['/users', '/role-management'], path: '/14-user-role-management/' },
  { match: '/notification-preferences', path: '/15-notifications/' },
];